import type { TransferCodeResponse } from '@xox/shared'
import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useCountdown } from '../hooks/useCountdown'
import { api } from '../lib/api'
import { clockFrom } from '../lib/clock'
import { useSession } from '../store/session'
import { JoinCodeForm } from './JoinCodeForm'
import { Button, Card } from './ui'

/**
 * Guest identities travel by code: this device shows one for ten minutes, the other device
 * enters it (or scans it) and becomes this player. A code works exactly once.
 */
export function TransferPanel() {
  const me = useSession((s) => s.me)
  const replace = useSession((s) => s.replace)
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [code, setCode] = useState<TransferCodeResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<string | null>(params.get('transfer'))
  const clock = useMemo(() => clockFrom(code?.serverNow ?? Date.now()), [code?.serverNow])
  const remaining = useCountdown(code?.expiresAt ?? null, clock)
  // A device that has never been played on has nothing to lose: switch without asking.
  const fresh = me !== null && me.nicknameAuto && me.progress.xp === 0

  const mint = async () => {
    setError(null)
    try {
      setCode(await api.transferCode())
    } catch (err) {
      setError((err as Error).message)
    }
  }
  const redeem = useCallback(
    async (c: string) => {
      setError(null)
      try {
        const next = await api.redeemTransfer(c)
        replace(next)
        setPending(null)
        navigate('/')
      } catch (err) {
        setError((err as Error).message)
      }
    },
    [replace, navigate],
  )

  const autoRedeemed = useRef(false)
  useEffect(() => {
    if (!pending || !fresh || autoRedeemed.current) return
    autoRedeemed.current = true
    void redeem(pending)
  }, [fresh, pending, redeem])

  const expired = code !== null && remaining !== null && remaining <= 0
  const url = code ? `${location.origin}/me?transfer=${code.code}` : ''

  return (
    <Card className="flex flex-col gap-4">
      <div>
        <h2 className="font-semibold">Play on another device</h2>
        <p className="text-sm text-slate-500">
          Your progress lives in this browser. Move it to your phone or laptop with a one-time code.
        </p>
      </div>
      {me && (
        <div className="flex flex-col gap-3">
          {code && !expired ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl bg-slate-100 p-4 dark:bg-slate-800">
              <div className="font-mono text-2xl font-black tracking-[0.3em] tabular-nums">
                {code.code.slice(0, 4)}-{code.code.slice(4)}
              </div>
              <QRCodeSVG value={url} size={128} className="rounded-lg bg-white p-2" />
              <p className="text-xs text-slate-500">
                Enter it on the other device within{' '}
                {Math.max(1, Math.ceil((remaining ?? 0) / 60000))} min. One use only.
              </p>
            </div>
          ) : (
            <Button variant="secondary" onClick={mint}>
              {expired ? 'Code expired — make a new one' : 'Show a transfer code'}
            </Button>
          )}
        </div>
      )}
      <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
        {pending ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm">
              {me
                ? `This device is currently ${me.nickname}. Switch to the transferred profile?`
                : 'Switch this device to the transferred profile?'}
            </p>
            <div className="flex gap-2">
              <Button onClick={() => redeem(pending)}>Switch</Button>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <JoinCodeForm
            id="transfer-code"
            label="Have a code from another device?"
            onJoin={(c) => (fresh ? redeem(c) : setPending(c))}
          />
        )}
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
    </Card>
  )
}
